<template>
    <main
        role="main"
        class="fa-v2"
        v-if="(appReference && loading) || !appReference"
    >
        <portal to="ariane">
            <div class="tbv2-page-title">{{ headerTitle }}</div>
        </portal>

        <header class="fa-hero">
            <router-link :to="`/forms/${formId}`" class="fa-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') || 'Retour' }}</span>
            </router-link>
            <h1 class="fa-hero-title">{{ headerTitle }}</h1>
            <p class="fa-hero-sub">{{ subtitleText }}</p>
        </header>

        <section class="fa-card">
            <header class="fa-card-header">
                <div class="fa-card-icon">
                    <i class="fal fa-code-branch"></i>
                </div>
                <div>
                    <h2 class="fa-card-title">{{ $t('forms.applications.section_identity') }}</h2>
                    <p class="fa-card-desc">{{ subtitleText }}</p>
                </div>
            </header>

            <form class="fa-form" @submit.prevent="createOrUpdateApplication">
                <div class="fa-field">
                    <label class="fa-label" for="appName">{{ $t('forms.applications.name') }}</label>
                    <input
                        id="appName"
                        v-model="application.name"
                        type="text"
                        class="fa-input"
                        required
                    />
                    <p v-if="isSend && nameMessage" class="fa-error">{{ nameMessage }}</p>
                </div>

                <div class="fa-field">
                    <label class="fa-label" for="appClientURL">{{ $t('forms.applications.client_url') }}</label>
                    <input
                        id="appClientURL"
                        v-model="application.client_url"
                        type="url"
                        class="fa-input"
                        placeholder="https://"
                    />
                    <p v-if="isSend && clientUrlMessage" class="fa-error">{{ clientUrlMessage }}</p>
                </div>

                <div class="fa-field">
                    <label class="fa-label" for="appSource">{{ $t('forms.applications.source') }}</label>
                    <input
                        id="appSource"
                        v-model="application.source"
                        type="text"
                        class="fa-input"
                        required
                    />
                    <p v-if="isSend && sourceMessage" class="fa-error">{{ sourceMessage }}</p>
                </div>

                <div class="fa-section-divider">
                    <span class="fa-section-label">{{ $t('forms.applications.section_routing') }}</span>
                </div>

                <div class="fa-grid-2">
                    <div class="fa-field">
                        <label class="fa-label" for="appLang">{{ $t('forms.applications.lang.label') }}</label>
                        <div class="fa-select-wrapper">
                            <select
                                id="appLang"
                                v-model="application.lang"
                                class="fa-input fa-select"
                            >
                                <option value="fr">{{ $t('forms.applications.lang.fr').trim() }}</option>
                                <option value="en">{{ $t('forms.applications.lang.en').trim() }}</option>
                            </select>
                            <i class="fal fa-chevron-down fa-select-caret"></i>
                        </div>
                    </div>

                    <div class="fa-field">
                        <label class="fa-label" for="appActive">{{ $t('forms.applications.active.label') }}</label>
                        <div class="fa-select-wrapper">
                            <select
                                id="appActive"
                                v-model="application.active"
                                class="fa-input fa-select"
                            >
                                <option value="1">{{ $t('forms.applications.active.1').trim() }}</option>
                                <option value="0">{{ $t('forms.applications.active.0').trim() }}</option>
                            </select>
                            <i class="fal fa-chevron-down fa-select-caret"></i>
                        </div>
                    </div>
                </div>

                <div class="fa-actions">
                    <router-link :to="`/forms/${formId}`" class="fa-btn fa-btn--ghost">
                        {{ $t('template.delete.cancel') }}
                    </router-link>
                    <button
                        type="submit"
                        class="fa-btn fa-btn--primary"
                        :disabled="isSendLoading"
                    >
                        <i v-if="isSendLoading" class="fas fa-circle-notch fa-spin"></i>
                        <i v-else class="fal fa-check"></i>
                        {{ $t('forms.applications.send') }}
                    </button>
                </div>

                <p v-if="isChangeSaved" class="fa-saved-msg">
                    <i class="fal fa-check-circle"></i>
                    {{ $t('forms.applications.saved') }}
                </p>
            </form>
        </section>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions } from 'vuex'

export default {
    name: 'FormApplications',
    data() {
        return {
            appReference: '',
            formId: null,
            loading: false,
            isChangeSaved: false,
            isSend: false,
            isSendLoading: false,
            nameMessage: '',
            sourceMessage: '',
            clientUrlMessage: '',
            regex: 'https?:\\/\\/(?:www\\.)?([-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b)*(\\/[\\/\\d\\w\\.-]*)*(?:[\\?])*(.+)*'
        }
    },
    created() {
        this.appReference = this.$route.params.reference
        this.formId = this.$route.params.id
        if (this.appReference) this.getAppData()
    },
    computed: {
        application: {
            get() {
                return this.$store.state.application.application
            },
            set(value) {
                this.$store.commit('application/setApplication', value)
            }
        },
        headerTitle() {
            if (this.appReference) {
                const name = this.application && this.application.name
                return name
                    ? `${this.$t('forms.applications.title_update')} "${name}"`
                    : this.$t('forms.applications.title_update')
            }
            return this.$t('forms.applications.title_create')
        },
        subtitleText() {
            return this.appReference
                ? this.$t('forms.applications.update_subtitle')
                : this.$t('forms.applications.create_subtitle')
        }
    },
    methods: {
        ...mapActions('application', [
            'getApplication',
            'createApplication',
            'editApplication'
        ]),
        getAppData() {
            this.getApplication({ formId: this.formId, id: this.appReference })
                .then(() => {
                    this.loading = true
                })
                .catch((error) => {
                    this.loading = false
                    this.$store.dispatch('dispatchError', error)
                })
        },
        createOrUpdateApplication() {
            this.confirmName()
            this.confirmSource()
            this.confirmClientUrl()
            this.isSend = true
            this.isSendLoading = true

            if (this.nameMessage || this.sourceMessage || this.clientUrlMessage) {
                this.isSendLoading = false
                return
            }

            const endpoint = this.appReference
                ? this.editApplication({
                      formId: this.formId,
                      id: this.appReference
                  })
                : this.createApplication(this.formId)

            endpoint
                .then(() => {
                    this.isChangeSaved = true
                    this.isSendLoading = false
                    this.$router.push(`/forms/${this.formId}`)
                })
                .catch((error) => {
                    this.isChangeSaved = false
                    this.isSendLoading = false
                    this.$store.dispatch('dispatchError', error)
                })
        },
        confirmName() {
            this.nameMessage = this.application.name ? '' : this.$t('error.validation.required')
        },
        confirmSource() {
            this.sourceMessage = this.application.source ? '' : this.$t('error.validation.required')
        },
        confirmClientUrl() {
            if (!this.application.client_url) {
                this.clientUrlMessage = this.$t('error.validation.required')
            } else if (!this.application.client_url.match(this.regex)) {
                this.clientUrlMessage = this.$t('error.validation.url_valid')
            } else {
                this.clientUrlMessage = ''
            }
        }
    }
}
</script>

<style scoped>
.fa-v2 {
    width: 100%;
    max-width: 780px;
    margin: 0 auto;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.fa-hero {
    margin-bottom: var(--v2-space-5);
}

.fa-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.fa-back:hover {
    color: var(--v2-primary);
}

.fa-back i {
    font-size: 0.75rem;
}

.fa-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.fa-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 640px;
}

.fa-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.fa-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.fa-card-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.05rem;
}

.fa-card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
}

.fa-card-desc {
    margin: 2px 0 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.fa-form {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.fa-section-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 6px;
    margin-top: 2px;
    border-top: 1px solid var(--v2-border-subtle);
}

.fa-section-label {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding-top: 14px;
}

.fa-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.fa-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.fa-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    letter-spacing: 0.01em;
    margin: 0;
}

.fa-input {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-family: inherit;
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.fa-input:hover {
    border-color: var(--v2-border-strong);
}

.fa-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.fa-input::placeholder {
    color: var(--v2-text-4);
}

.fa-select-wrapper {
    position: relative;
}

.fa-select {
    padding-right: 34px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    background: var(--v2-surface-0);
}

.fa-select-caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.75rem;
    color: var(--v2-text-3);
    pointer-events: none;
}

.fa-error {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-danger);
    font-weight: var(--v2-font-weight-medium);
}

.fa-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid var(--v2-border);
    margin-top: 4px;
}

.fa-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    border: 1px solid transparent;
    cursor: pointer;
    text-decoration: none !important;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
}

.fa-btn i {
    font-size: 0.8rem;
}

.fa-btn--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.fa-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.fa-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.fa-btn--primary i {
    color: #fff !important;
}

.fa-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.fa-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.fa-saved-msg {
    display: inline-flex;
    align-self: flex-end;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-success);
    font-weight: var(--v2-font-weight-semibold);
}

.fa-saved-msg i {
    font-size: 0.85rem;
}

@media (max-width: 900px) {
    .fa-v2 {
        padding: 16px 18px 32px;
    }

    .fa-hero-title {
        font-size: 1.375rem;
    }

    .fa-grid-2 {
        grid-template-columns: 1fr;
    }

    .fa-form {
        padding: 16px;
    }

    .fa-card-header {
        padding: 14px 16px;
    }

    .fa-actions {
        flex-direction: column-reverse;
        align-items: stretch;
    }

    .fa-btn {
        justify-content: center;
    }
}
</style>
